import React, { useState, useEffect, useCallback } from 'react';
import { X, Radio } from 'lucide-react';
import { Button } from './ui/button';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';

export default function AnnouncementBar() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  
  const [announcement, setAnnouncement] = useState({
    enabled: false,
    message: '',
    is_live: false,
    live_title: '',
    live_host: '',
    stream_url: ''
  });
  const [showLiveViewer, setShowLiveViewer] = useState(false);

  const fetchAnnouncement = useCallback(async () => {
    try {
      const response = await apiClient.get('/announcement');
      setAnnouncement(response.data);
      // Auto-show live viewer for non-admins when live
      if (response.data.is_live && !isAdmin) {
        setShowLiveViewer(true);
      }
    } catch (error) {
      // Silently fail - announcement is optional
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchAnnouncement();
    const interval = setInterval(fetchAnnouncement, 5000);
    return () => clearInterval(interval);
  }, [fetchAnnouncement]);

  // Convert YouTube/Twitch URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    if (url.includes('youtube.com/live')) {
      const videoId = url.split('live/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    
    // Twitch
    if (url.includes('twitch.tv/')) {
      const channel = url.split('twitch.tv/')[1]?.split('?')[0];
      return `https://player.twitch.tv/?channel=${channel}&parent=${window.location.hostname}`;
    }
    
    return url;
  };

  // Only show public announcement bar (no admin controls here anymore)
  if (!announcement.enabled && !announcement.is_live) {
    return null;
  }

  return (
    <>
      {/* Public Announcement Bar */}
      {announcement.enabled && announcement.message && (
        <div className="fixed top-16 left-0 right-0 z-30 bg-gradient-to-r from-violet-600 to-fuchsia-600">
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-center gap-3">
              {announcement.is_live && (
                <button 
                  onClick={() => setShowLiveViewer(true)}
                  className="flex items-center gap-2 bg-red-500 px-3 py-1 rounded-full animate-pulse hover:bg-red-400 transition"
                >
                  <Radio size={12} />
                  <span className="text-xs font-bold">LIVE - Click to Watch</span>
                </button>
              )}
              <p className="text-white text-sm font-medium">{announcement.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Spacer div to push content down when announcement is visible */}
      {announcement.enabled && announcement.message && (
        <div style={{ height: '40px' }} />
      )}

      {/* Live Video Viewer Modal - For ALL users */}
      {showLiveViewer && announcement.is_live && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm">
          <div className="w-full max-w-5xl mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-red-600 px-4 py-2 rounded-full animate-pulse">
                  <Radio size={16} />
                  <span className="font-bold">LIVE</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{announcement.live_title || 'Live Announcement'}</h2>
                  {announcement.live_host && (
                    <p className="text-white/60 text-sm">Hosted by {announcement.live_host}</p>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                onClick={() => setShowLiveViewer(false)}
                className="text-white/60 hover:text-white"
              >
                <X size={24} />
              </Button>
            </div>

            {/* Video Container */}
            <div className="relative aspect-video bg-black rounded-2xl overflow-hidden border-2 border-violet-500/50 shadow-[0_0_50px_rgba(139,92,246,0.3)]">
              {announcement.stream_url ? (
                <iframe
                  src={getEmbedUrl(announcement.stream_url)}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <Radio className="w-16 h-16 text-red-500 mx-auto mb-4 animate-pulse" />
                    <p className="text-white text-xl font-bold">Live Broadcast</p>
                    <p className="text-white/60">{announcement.live_title}</p>
                  </div>
                </div>
              )}
            </div>
            
            <p className="text-center text-white/40 text-sm mt-4">
              Press ESC or click X to minimize
            </p>
          </div>
        </div>
      )}
    </>
  );
}
